import { PhpArray, phpCopy, type PhpKey, type PhpKeyInput } from '../../php-compat';
import type { PhpRef, PortEnv } from '../../port-contract';
import { entries, get, isArray, type Storage } from './shared';
import { signatures, type DependencyName, type DispatchDeps } from './dependency-signatures';

export type DispatchEnv = PortEnv<DispatchDeps>;
export type Ref = PhpRef<unknown>;
/** An explicit reference argument keeps an array with a `value` key unambiguous. */
export const r = (reference: Ref): { reference: Ref } => ({ reference });
export function call(env: DispatchEnv, name: DependencyName, args: Record<string, unknown>): unknown {
    const params = signatures[name];
    const last = Math.max(-1, ...params.map((p, i) => Object.prototype.hasOwnProperty.call(args, p.name) ? i : -1));
    const positional = params.slice(0, last + 1).map(p => {
        const arg = Object.prototype.hasOwnProperty.call(args, p.name) ? args[p.name] : phpCopy('default' in p ? p.default : null);
        const reference = arg !== null && typeof arg === 'object' && 'reference' in arg ? (arg as { reference: Ref }).reference : null;
        if (p.ref) return reference ?? { value: arg };
        return reference ? reference.value : arg;
    });
    return (env.deps[name] as (...args: unknown[]) => unknown)(...positional);
}
/** PHP nested assignment creates arrays on demand and preserves reference boxes. */
export function put(reference: Ref, path: PhpKeyInput[], value: unknown): null {
    if (!isArray(reference.value)) reference.value = new PhpArray();
    let row = reference.value as Storage;
    for (const key of path.slice(0, -1)) {
        let child = get(row, typeof key === 'bigint' ? String(key) : key as string | number);
        if (!isArray(child)) { child = new PhpArray(); write(row, key, child); }
        row = child as Storage;
    }
    write(row, path[path.length - 1]!, phpCopy(value));
return null;
}
function write(row: Storage, key: PhpKeyInput, value: unknown): void {
    if (row instanceof PhpArray) row.set(key, value);
    else (row as Record<string, unknown>)[String(key)] = value;
}
export function append(reference: Ref, value: unknown): null {
    if (!isArray(reference.value)) reference.value = new PhpArray();
    if (reference.value instanceof PhpArray) reference.value.append(phpCopy(value));
    else {
        const ordered = new PhpArray(entries(reference.value)); ordered.append(phpCopy(value)); reference.value = ordered;
    }
return null;
}
export function remove(reference: Ref, ...path: PhpKey[]): null {
    const row = get(reference.value, ...path.slice(0, -1).map(k => typeof k === 'bigint' ? String(k) : k));
    if (row instanceof PhpArray) row.delete(path[path.length - 1]!);
    else if (isArray(row)) delete (row as Record<string, unknown>)[String(path[path.length - 1])];
return null;
}
